import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useBranding } from '../lib/branding.jsx';

export default function AdminBranding() {
  const { user } = useAuth();
  const { branding, refresh, applyPreview } = useBranding();
  const [form, setForm] = useState(branding);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    // Normalize any legacy free-format phone strings stored in the DB so the
    // masked PhoneField renders them correctly. Strip everything but digits
    // and keep the last 10 (drops country codes / extensions).
    const norm = (v) => {
      const d = String(v || '').replace(/\D/g, '');
      return d.length > 10 ? d.slice(-10) : d;
    };
    setForm({
      ...branding,
      hospital_phone:   norm(branding.hospital_phone),
      pharmacy_phone:   norm(branding.pharmacy_phone),
      it_support_phone: norm(branding.it_support_phone),
      help_phone:       norm(branding.help_phone),
    });
  }, [branding.id, branding.display_name]);

  function update(field, v) {
    const next = { ...form, [field]: v };
    setForm(next);
    applyPreview(next); // live preview without saving
  }

  async function save(e) {
    e.preventDefault();
    // Client-side gate: phones must be empty or exactly 10 digits.
    const phones = ['hospital_phone', 'pharmacy_phone', 'it_support_phone', 'help_phone'];
    for (const k of phones) {
      const d = String(form[k] || '').replace(/\D/g, '');
      if (d.length > 0 && d.length !== 10) {
        setErr(`${k.replace(/_/g, ' ')} must be a 10-digit US phone number (currently ${d.length} digits).`);
        return;
      }
    }
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.updateBranding({
        display_name: form.display_name || null,
        logo_url: form.logo_url || null,
        theme_primary: form.theme_primary,
        theme_accent: form.theme_accent,
        // Phase 6 - landing hero + contact directory + custom FAQs
        hero_tagline: form.hero_tagline ?? null,
        hero_subtext: form.hero_subtext ?? null,
        hospital_phone:   form.hospital_phone   || null,
        pharmacy_phone:   form.pharmacy_phone   || null,
        it_support_phone: form.it_support_phone || null,
        help_phone:       form.help_phone       || null,
        help_email:       form.help_email       || null,
        custom_faqs: Array.isArray(form.custom_faqs)
          ? form.custom_faqs
              .map(f => ({ q: (f.q || '').trim(), a: (f.a || '').trim() }))
              .filter(f => f.q && f.a)
          : [],
      });
      await refresh();
      setMsg('Branding saved. All users in this facility will see the new theme on their next page load.');
    } catch (e) {
      setErr(e.body?.message || e.message);
    } finally { setBusy(false); }
  }

  function setFaqAt(i, key, v) {
    const list = Array.isArray(form.custom_faqs) ? [...form.custom_faqs] : [];
    list[i] = { ...(list[i] || { q: '', a: '' }), [key]: v };
    update('custom_faqs', list);
  }
  function addFaq() {
    const list = Array.isArray(form.custom_faqs) ? [...form.custom_faqs] : [];
    list.push({ q: '', a: '' });
    update('custom_faqs', list);
  }
  function removeFaq(i) {
    const list = Array.isArray(form.custom_faqs) ? [...form.custom_faqs] : [];
    list.splice(i, 1);
    update('custom_faqs', list);
  }

  if (user?.role !== 'admin') {
    return <div className="card p-6 text-red-700 font-medium">Admin role required</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Branding</h1>
        <p className="text-sm text-slate-500">
          Customize the look for <strong>{branding.name}</strong>. Changes apply to every user in this facility only.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <form onSubmit={save} className="card p-4 space-y-4 md:col-span-2">
          <label className="block text-sm">
            <span className="text-xs text-slate-600">Display name (shown in sidebar + browser tab)</span>
            <input className="input" value={form.display_name || ''}
                   onChange={e => update('display_name', e.target.value)} />
          </label>

          <label className="block text-sm">
            <span className="text-xs text-slate-600">Logo URL (PNG, JPG, or SVG, served over HTTPS)</span>
            <input className="input" placeholder="https://…/logo.png" value={form.logo_url || ''}
                   onChange={e => update('logo_url', e.target.value)} />
            <span className="text-xs text-slate-500">Square works best. ~64x64 minimum. Use a CDN-hosted URL.</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-xs text-slate-600">Primary color</span>
              <div className="flex gap-2 items-center">
                <input type="color" className="h-9 w-12 rounded border border-slate-300"
                       value={form.theme_primary || '#1d4ed8'}
                       onChange={e => update('theme_primary', e.target.value)} />
                <input className="input font-mono" value={form.theme_primary || ''}
                       onChange={e => update('theme_primary', e.target.value)} />
              </div>
            </label>
            <label className="block text-sm">
              <span className="text-xs text-slate-600">Accent color</span>
              <div className="flex gap-2 items-center">
                <input type="color" className="h-9 w-12 rounded border border-slate-300"
                       value={form.theme_accent || '#0ea5e9'}
                       onChange={e => update('theme_accent', e.target.value)} />
                <input className="input font-mono" value={form.theme_accent || ''}
                       onChange={e => update('theme_accent', e.target.value)} />
              </div>
            </label>
          </div>

          {/* Phase 6 - Landing hero (per-facility marketing copy) */}
          <fieldset className="border border-slate-200 rounded p-3 space-y-3">
            <legend className="text-xs uppercase tracking-wide text-slate-500 px-1">Landing page hero</legend>
            <label className="block text-sm">
              <span className="text-xs text-slate-600">Tagline (large headline)</span>
              <input className="input" maxLength={140}
                placeholder="Your medications. Delivered to your bedside."
                value={form.hero_tagline || ''} onChange={e => update('hero_tagline', e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-slate-600">Sub-text (one-paragraph promise to the patient)</span>
              <textarea className="input" rows={2} maxLength={400}
                placeholder="Skip the pharmacy stop on the way home..."
                value={form.hero_subtext || ''} onChange={e => update('hero_subtext', e.target.value)} />
            </label>
          </fieldset>

          {/* Phase 6 - Public contact directory shown on /help/faq */}
          <fieldset className="border border-slate-200 rounded p-3 space-y-3">
            <legend className="text-xs uppercase tracking-wide text-slate-500 px-1">Public contact directory (FAQ page)</legend>
            <div className="grid sm:grid-cols-2 gap-3">
              <PhoneField label="Hospital main line"   value={form.hospital_phone}   onChange={v => update('hospital_phone',   v)} />
              <PhoneField label="Pharmacy"             value={form.pharmacy_phone}   onChange={v => update('pharmacy_phone',   v)} />
              <PhoneField label="IT support"           value={form.it_support_phone} onChange={v => update('it_support_phone', v)} />
              <PhoneField label="Help desk"            value={form.help_phone}       onChange={v => update('help_phone',       v)} />
              <label className="block text-sm sm:col-span-2">
                <span className="text-xs text-slate-600">Help email</span>
                <input type="email" className="input" placeholder="help@yourhospital.org"
                  value={form.help_email || ''} onChange={e => update('help_email', e.target.value)} />
              </label>
            </div>
            <div className="text-xs text-slate-500">
              These appear on your public <em>Help &amp; FAQ</em> page. Leave any field blank to hide it.
            </div>
          </fieldset>

          {/* Phase 6 - Custom FAQs */}
          <fieldset className="border border-slate-200 rounded p-3 space-y-3">
            <legend className="text-xs uppercase tracking-wide text-slate-500 px-1">Custom FAQs (optional)</legend>
            {(form.custom_faqs || []).map((f, i) => (
              <div key={i} className="border border-slate-200 rounded p-2 space-y-2 bg-slate-50/40">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Q&amp;A {i + 1}</span>
                  <button type="button" className="ml-auto text-xs text-red-700 hover:underline"
                    onClick={() => removeFaq(i)}>Remove</button>
                </div>
                <input className="input" placeholder="Question" maxLength={240}
                  value={f.q || ''} onChange={e => setFaqAt(i, 'q', e.target.value)} />
                <textarea className="input" rows={2} placeholder="Answer" maxLength={4000}
                  value={f.a || ''} onChange={e => setFaqAt(i, 'a', e.target.value)} />
              </div>
            ))}
            <button type="button" className="btn-ghost" onClick={addFaq}>+ Add FAQ</button>
            <div className="text-xs text-slate-500">
              These layer on top of built-in answers about how the app works, who can use it, and how data is protected.
              Up to 50 entries.
            </div>
          </fieldset>

          {err && <div className="text-sm text-red-600">{err}</div>}
          {msg && <div className="text-sm text-emerald-700">{msg}</div>}

          <div className="flex gap-2">
            <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save branding'}</button>
            <button type="button" className="text-sm text-slate-600 hover:underline"
                    onClick={() => { setForm(branding); applyPreview(branding); }}>
              Reset to saved
            </button>
          </div>
        </form>

        <div className="card p-4 space-y-3">
          <div className="text-xs uppercase text-slate-500 tracking-wide">Live preview</div>
          <div className="rounded border border-slate-200 p-3 flex items-center gap-3" style={{ borderColor: 'var(--brand-primary)' }}>
            {form.logo_url
              ? <img src={form.logo_url} alt="" className="w-12 h-12 rounded" />
              : <div className="w-12 h-12 rounded" style={{ background: form.theme_primary }} />}
            <div>
              <div className="font-bold" style={{ color: form.theme_primary }}>{form.display_name || 'M2B'}</div>
              <div className="text-xs text-slate-500">Meds-to-Beds</div>
            </div>
          </div>
          <button type="button" className="btn-primary w-full" style={{ background: form.theme_primary }}>
            Sample primary button
          </button>
          <div className="text-sm">
            <span className="px-2 py-0.5 rounded text-white text-xs" style={{ background: form.theme_accent }}>accent badge</span>
          </div>
          <div className="text-xs text-slate-500">
            The sidebar and active nav links use the primary color. Live preview is applied to the running page so you can see exactly how it looks before saving.
          </div>
        </div>
      </div>
    </div>
  );
}

function PhoneField({ label, value, onChange }) {
  // Stored as a digits-only string (e.g. "5555550100"). Display masked as
  // "(555) 555-0100". Strip non-digits as the user types and cap at 10 digits.
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  const display = formatPhoneInput(digits);
  const handleChange = (e) => {
    let d = e.target.value.replace(/\D/g, '');
    // If user pasted "+1NNNNNNNNNN" (11 digits starting with US country code 1),
    // drop the leading 1 so the area code lands correctly.
    if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
    onChange(d.slice(0, 10));
  };
  const incomplete = digits.length > 0 && digits.length < 10;
  return (
    <label className="block text-sm">
      <span className="text-xs text-slate-600">{label}</span>
      <input type="tel" inputMode="numeric" className="input font-mono"
        placeholder="(555) 555-0100" maxLength={14}
        value={display} onChange={handleChange} />
      {incomplete && (
        <span className="text-xs text-amber-700">Need {10 - digits.length} more digit{10 - digits.length === 1 ? '' : 's'} (10-digit US number).</span>
      )}
    </label>
  );
}

function formatPhoneInput(d) {
  if (!d) return '';
  if (d.length <= 3)  return `(${d}`;
  if (d.length <= 6)  return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}
